import type { FC } from "react";
import { useCallback, useEffect, useRef, useState } from "react";

import { HeroContent } from "@ui/components/HeroV2/HeroContent";
import { SwiperJti } from "@ui/components/SwiperJti";
import { useHeroHeightLock } from "@ui/hooks/useHeroHeightLock";
import { useUser } from "@ui/hooks/useUser";
import classNames from "classnames";
import { SwiperSlide } from "swiper/react";
import type { Swiper as SwiperClass } from "swiper/types";

import { HeroBackground } from "./HeroBackground/HeroBackground";
import type { HeroV2Props } from "./types/HeroV2Types";
import { buildHeroContent } from "./utils/buildHeroContent";
import { getAutoplayDelayMs } from "./utils/getAutoplayDelayMs";

import { Promotions } from "./Promotions";

import styles from "./HeroContentAuthoringWrapper/HeroContentAuthoringWrapper.module.scss";

export const HeroV2: FC<HeroV2Props> = ({
  data = [],
  isContainerWidth = false,
  isViewportHeight = true,
  withLoader = false,
  autoplayEnabled = false,
  autoplayDelayInSeconds = 5,
}) => {
  const { isUserLoggedIn } = useUser();
  const promoRefs = useRef<Array<HTMLDivElement | null>>([]);
  const [promoHeights, setPromoHeights] = useState<number[]>([]);
  const [currentSlide, setCurrentSlide] = useState(0);
  const { heroRef, lockedWidthRef } = useHeroHeightLock({ enabled: isViewportHeight });

  const recalculateHeight = useCallback(() => {
    const newPromoHeights = promoRefs.current.map((promoRef) => promoRef?.offsetHeight || 0);
    setPromoHeights(newPromoHeights);
  }, [setPromoHeights]);

  useEffect(() => {
    recalculateHeight();

    const handleResize = () => {
      if (window.innerWidth === lockedWidthRef.current) return;

      recalculateHeight();
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [data, recalculateHeight, lockedWidthRef]);

  const handleSlideChange = useCallback(
    (swiper: SwiperClass) => {
      setCurrentSlide(swiper.realIndex);
    },
    [setCurrentSlide]
  );

  useEffect(() => {
    const pagination = document.querySelector(".swiper-pagination-bullets") as HTMLElement;
    // basic bottom value from layouts;
    let bottom = 28;
    if (pagination && Boolean(promoHeights[currentSlide])) {
      bottom += promoHeights[currentSlide];
    }

    pagination.style.bottom = `${bottom}px`;
  }, [promoHeights, currentSlide]);

  const [isVideoLoaded, setIsVideoLoaded] = useState(false);
  const isFirstElementVideo = data[0]?.backgroundType === "video";
  const showLoader = withLoader && !isVideoLoaded && isFirstElementVideo;

  return (
    <div ref={heroRef}>
      <SwiperJti
        isViewportHeight={isViewportHeight}
        isCustomPagination
        loop
        isContainerWidth={isContainerWidth}
        handleSlideChange={handleSlideChange}
        showLoader={showLoader}
        isShowNavigationArrows={data.length > 1}
        isAutoplayEnabled={autoplayEnabled}
        autoplayDelay={getAutoplayDelayMs(autoplayDelayInSeconds)}
        resizeObserver={false}
        observer={false}
      >
        {data.map((slide, index) => {
          const discounts = slide?.content?.discounts;
          const hasPromotionDisclaimers = discounts && discounts.filter((d) => !!d.priceDisclaimer).length > 0;
          const isFirstElement = index === 0 && isFirstElementVideo;

          return (
            <SwiperSlide key={`${slide.content?.title}--${index}`}>
              <>
                <HeroBackground
                  backgroundType={slide.backgroundType}
                  desktopImage={slide.desktopImage}
                  mediumImage={slide.mediumImage}
                  smallImage={slide.smallImage}
                  tabletVideoFileReference={slide.tabletVideoFileReference}
                  tabletVideoFileType={slide.tabletVideoFileType}
                  mobileVideoFileReference={slide.mobileVideoFileReference}
                  mobileVideoFileType={slide.mobileVideoFileType}
                  videoFileReference={slide.videoFileReference}
                  videoFileType={slide.videoFileType}
                  removeVideoRoundedCorners={slide.removeVideoRoundedCorners}
                  renderBackgroundImage={slide.renderBackgroundImage}
                  setIsVideoLoaded={setIsVideoLoaded}
                  isFirstElement={isFirstElement}
                />
                <div
                  className={classNames(styles.content, {
                    [styles.contentContainerWidth]: isContainerWidth,
                    [styles.contentContainerWidthWithPromotions]: isContainerWidth && hasPromotionDisclaimers,
                  })}
                >
                  <HeroContent {...buildHeroContent(slide, isUserLoggedIn, isContainerWidth)} />
                  <div className={styles.bottom}>
                    {(hasPromotionDisclaimers || slide.legalDisclaimer) && (
                      <Promotions
                        discounts={discounts}
                        legalDisclaimer={slide.legalDisclaimer}
                        ref={(el) => {
                          promoRefs.current[index] = el;
                        }}
                        theme={slide.theme || undefined}
                      />
                    )}
                  </div>
                </div>
              </>
            </SwiperSlide>
          );
        })}
      </SwiperJti>
    </div>
  );
};
